// marketingTags.ts — CORE-1142
//
// Внешний маркетинговый слой: Яндекс.Метрика / Google Analytics / пиксель VK.
// Здесь — только чистые правила (что, кому, когда и с какими параметрами).
// Всё, что трогает DOM и глобальные объекты площадок, живёт в
// `marketingTagRuntime.ts`.
//
// ── Что здесь source of truth, а что нет ────────────────────────────────────
// Внутренняя `AnalyticsEvents` остаётся единственным источником продуктовой
// правды. Внешние системы получают ПОДМНОЖЕСТВО событий — конверсии, — и их
// числа заведомо меньше внутренних: блокировщики, отказ от согласия и
// дедупликация режут ровно этот слой и не трогают внутренний. Расхождение
// счётчиков здесь — норма, а не расследование; см. docs/marketing-analytics.md.
//
// ── Почему схема живёт в конфиге, а не в коде ───────────────────────────────
// На встрече 11.08.2026 обсуждалась связка «RU → Метрика, non-RU → GA,
// DataCraft сверху», но финальной она не была: открытыми остались и отдельный
// домен, и состав систем. Захардкодить обсуждавшийся вариант значило бы
// зафиксировать в коде решение, которого ещё нет. Поэтому схема целиком
// задаётся одной переменной окружения, а код знает лишь КАК подключить
// площадку определённого вида — не КОГО подключать.
//
//   EXPO_PUBLIC_MARKETING_TAGS="metrika:12345678:ru;ga4:G-XXXXXXX:intl"
//
// Пустая или отсутствующая переменная — слой выключен целиком и не грузит
// ничего. Это и есть состояние по умолчанию, включая прод: ни один счётчик не
// заводится сам собой.

import type { AcquisitionState } from './acquisition';
import { resolveSentryEnvironment } from '../config/sentryEnvironment';
import type { CookieCategory, CookieChoice } from './cookieChoice';

// ── Виды площадок ───────────────────────────────────────────────────────────

/**
 * Виды, которые умеет подключать рантайм. Список расширяемый: добавить
 * площадку — значит добавить сюда вид и загрузчик в `marketingTagRuntime`,
 * не трогая ни правила, ни разметку событий.
 */
export type TagProviderKind = 'metrika' | 'ga4' | 'vk';

const KNOWN_KINDS: readonly TagProviderKind[] = ['metrika', 'ga4', 'vk'];

/**
 * Аудитория, которой площадка положена.
 *
 * `unknown` — это не «третий регион», а «регион ещё не известен». Он есть
 * потому, что GeoIP отвечает не мгновенно, а таймзонная эвристика ошибается на
 * экспатах. Значение отдельным состоянием, а не «по умолчанию intl», намеренно:
 * именно вопрос «можно ли грузить GA российскому пользователю» на встрече
 * 11.08 был поднят и остался открытым, и молчаливое «пока не знаем — считаем
 * не-Россией» дало бы на него ответ явочным порядком.
 */
export type TagRegion = 'ru' | 'intl' | 'unknown';

export interface TagProvider {
  kind: TagProviderKind;
  /** Идентификатор счётчика площадки. */
  id: string;
  /** Кому эта площадка положена. Пустой список — никому. */
  regions: ReadonlyArray<Exclude<TagRegion, 'unknown'>>;
}

/**
 * Регионы по умолчанию, если в конфиге они не указаны. Это ДЕФОЛТ, а не
 * правило: строка конфига перебивает его целиком.
 */
const DEFAULT_REGIONS: Record<TagProviderKind, ReadonlyArray<'ru' | 'intl'>> = {
  metrika: ['ru'],
  vk: ['ru'],
  ga4: ['intl'],
};

/** Разобрать `metrika:12345:ru;ga4:G-X:intl,ru` в список площадок. */
export function parseTagConfig(raw: string | null | undefined): TagProvider[] {
  const out: TagProvider[] = [];
  if (!raw) return out;
  for (const chunk of String(raw).split(';')) {
    const parts = chunk.trim().split(':');
    if (parts.length < 2) continue;
    const kind = parts[0].trim().toLowerCase() as TagProviderKind;
    if (!KNOWN_KINDS.includes(kind)) continue;
    const id = parts[1].trim();
    if (!id) continue;
    const regionsRaw = (parts[2] || '').trim().toLowerCase();
    let regions: ReadonlyArray<'ru' | 'intl'>;
    if (!regionsRaw) regions = DEFAULT_REGIONS[kind];
    else if (regionsRaw === 'all') regions = ['ru', 'intl'];
    else {
      regions = regionsRaw
        .split(',')
        .map((r) => r.trim())
        .filter((r): r is 'ru' | 'intl' => r === 'ru' || r === 'intl');
    }
    if (!regions.length) continue;
    // Один вид — один счётчик. Повтор в строке почти всегда опечатка, а два
    // одинаковых пикселя на странице означают удвоенную конверсию в кабинете.
    if (out.some((p) => p.kind === kind)) continue;
    out.push({ kind, id, regions });
  }
  return out;
}

/**
 * Окружения, которым разрешено писать в маркетинговый слой.
 *
 * По умолчанию только `production`, и это ответ на критерий «test/staging не
 * загрязняют production marketing reports»: разделение обеспечивается не
 * дисциплиной, а тем, что на стендах слой физически не включается. Стенд можно
 * поднять на ОТДЕЛЬНЫЙ тестовый счётчик, задав и окружение, и свой id — но
 * рабочие идентификаторы туда не попадут, потому что переменные разные.
 */
export function parseEnvironments(raw: string | null | undefined): string[] {
  const value = (raw || '').trim().toLowerCase();
  if (!value) return ['production'];
  return value.split(',').map((v) => v.trim()).filter(Boolean);
}

/** Разрешено ли этому окружению отправлять маркетинговые события. */
export function environmentAllows(host: string | null | undefined, allowed: string[]): boolean {
  return allowed.includes(resolveSentryEnvironment(host));
}

// ── Регион ──────────────────────────────────────────────────────────────────

/**
 * Из кода страны — аудитория.
 *
 * `null` (GeoIP не ответил, кэша нет) даёт `unknown`, а не `intl`: см. коммент
 * к `TagRegion`. Пока регион не известен, грузятся только площадки, положенные
 * всем регионам (CORE-1853, см. `shouldLoadTags`).
 */
export function resolveTagRegion(country: string | null | undefined): TagRegion {
  const value = (country || '').trim().toUpperCase();
  if (!value) return 'unknown';
  return value === 'RU' ? 'ru' : 'intl';
}

/** Площадки, положенные этой аудитории. */
export function providersForRegion(providers: TagProvider[], region: TagRegion): TagProvider[] {
  if (region === 'unknown') return [];
  return providers.filter((p) => p.regions.includes(region));
}

// ── Согласие ────────────────────────────────────────────────────────────────

/**
 * CORE-1659 — выбор человека по категориям cookie. `unknown` — ещё не отвечал
 * (или ответ не прочитан): в этом состоянии грузятся только необходимые.
 *
 * CORE-1775 — `blocked`: сторонние счётчики не грузятся вовсе, включая
 * необходимые (14–17 лет, см. `effectiveCookieChoice`).
 */
export type MarketingConsent = CookieChoice | 'unknown' | 'blocked';

/** Категория площадки: одна из выбираемых или `necessary` — грузится без выбора. */
export type TagCategory = CookieCategory | 'necessary';

/**
 * К какой категории cookie относится площадка. GA4 — аналитическая, пиксель
 * VK — маркетинговый (решение владельца 11.10.2026). Счётчик грузится только при
 * разрешённой своей категории.
 *
 * CORE-1775 — Метрика в необходимых (решение владельца 19.10.2026): отказ от
 * аналитических cookie оставлял нас без Метрики. Грузится сразу, до ответа на
 * баннер, и при любом ответе.
 */
export const TAG_CATEGORY: Record<TagProviderKind, TagCategory> = {
  metrika: 'necessary',
  ga4: 'analytics',
  vk: 'marketing',
};

/** Площадки, чья категория разрешена. */
export function providersForConsent(providers: TagProvider[], consent: MarketingConsent): TagProvider[] {
  if (consent === 'blocked') return [];
  return providers.filter((p) => {
    const category = TAG_CATEGORY[p.kind];
    if (category === 'necessary') return true;
    return consent !== 'unknown' && consent[category];
  });
}

/**
 * Полное решение «грузить ли внешние счётчики прямо сейчас».
 *
 * Собрано в одну функцию намеренно: гейтов четыре (согласие, окружение, регион,
 * наличие конфига), и разложенные по трём местам они рано или поздно
 * разъезжаются — а цена ошибки здесь в том, что чужой трекер загрузится там,
 * где загружаться не должен.
 */
export function shouldLoadTags(input: {
  consent: MarketingConsent;
  region: TagRegion;
  providers: TagProvider[];
  host: string | null | undefined;
  environments: string[];
}): { load: boolean; providers: TagProvider[]; reason: string } {
  if (!input.providers.length) return { load: false, providers: [], reason: 'no-config' };
  if (!environmentAllows(input.host, input.environments)) {
    return { load: false, providers: [], reason: 'environment' };
  }
  const allowed = providersForConsent(input.providers, input.consent);
  if (!allowed.length) return { load: false, providers: [], reason: 'consent' };
  // CORE-1853 — площадке, положенной всем регионам, ответ GeoIP ничего не решает,
  // и ждать его значит терять визиты: GeoIP не ответил, таймзона не российская —
  // и Метрика (на проде `metrika:…:all`) не загружалась вовсе.
  const chosen = input.region === 'unknown'
    ? allowed.filter(coversEveryRegion)
    : providersForRegion(allowed, input.region);
  if (!chosen.length) {
    return { load: false, providers: [], reason: input.region === 'unknown' ? 'region-unknown' : 'region-empty' };
  }
  return { load: true, providers: chosen, reason: 'ok' };
}

function coversEveryRegion(provider: TagProvider): boolean {
  return provider.regions.includes('ru') && provider.regions.includes('intl');
}

// ── Сопоставление событий и целей ───────────────────────────────────────────

/**
 * Имена целей во внешних системах. Ровно один словарь на все площадки: цель с
 * одинаковым смыслом обязана называться одинаково в Метрике, GA и пикселе,
 * иначе сводный отчёт складывает разные вещи в одну колонку.
 *
 * Имена латиницей и в snake_case — это требование самих кабинетов (в Метрике
 * идентификатор цели, в GA4 имя события), а не стилевое предпочтение.
 */
export const MarketingGoals = {
  registrationStart: 'registration_start',
  registrationSuccess: 'registration_success',
  activation: 'activation',
  practiceCompleted: 'practice_completed',
  /**
   * Слот под покупку. Клиентского события покупки сегодня не существует:
   * платёжный контур на стенде (CORE-1115) ещё не выпущен в прод. Имя
   * зафиксировано здесь заранее, чтобы кабинет и отчёты завели цель один раз, —
   * но правила, которое его отправляет, ниже нет, и заводить его «на будущее»
   * нельзя: цель, которая никогда не срабатывает, в кабинете неотличима от
   * сломанной.
   */
  purchase: 'purchase',

  // ── Восемь целей Adventum (CORE-1277) ─────────────────────────────────────
  //
  // Инструкция агентства перечисляет восемь действий (и ошибочно называет их
  // «четырьмя целями» в одном месте — считать надо перечисленное). Имена ниже
  // наши, а не из инструкции: в ней цели названы по-русски и по видимой надписи
  // («Клик "Календарь"»), а надпись на главной приходит с бэка и меняется
  // редактором — цель, привязанная к тексту, умирает от правки копирайта.
  //
  // Двух «Календарей» два ИМЕНИ, а не один с параметром: в кабинете цель — это
  // идентификатор, и разделять их фильтром по параметру пришлось бы в каждом
  // отчёте вручную. Параметр `goal_source` при этом тоже уезжает — им их и
  // различают, когда цели смотрят рядом.
  siteVisit: 'site_visit',
  homeLearnMore: 'home_learn_more',
  calendarFromEvents: 'calendar_from_events',
  calendarNav: 'calendar_nav',
  practicesNav: 'practices_nav',
  mastersNav: 'masters_nav',
  registrationStartPath: 'registration_start_path',
  registrationLiveSession: 'registration_live_session',
} as const;

export type MarketingGoal = (typeof MarketingGoals)[keyof typeof MarketingGoals];

/**
 * Единый каталог восьми целей Adventum: имя в кабинете ↔ формулировка из
 * инструкции ↔ действие в интерфейсе.
 *
 * Нужен затем, что стороны говорят о целях РАЗНЫМИ словами: агентство — по
 * видимой надписи, кабинет — по идентификатору, код — по внутреннему событию.
 * Пока эти три словаря не сведены в одном месте, сверка «а почему цель не
 * растёт» каждый раз начинается с выяснения, о какой цели вообще речь.
 *
 * Таблица «действие → внутреннее событие → цель» целиком — в
 * `docs/marketing-analytics.md`, §5.1.
 */
export const ADVENTUM_GOALS: ReadonlyArray<{
  goal: MarketingGoal;
  /** Как цель названа в инструкции агентства. */
  title: string;
  /** Где человек нажимает. */
  surface: string;
}> = [
  { goal: MarketingGoals.siteVisit, title: 'Посетил сайт', surface: 'любая страница прода' },
  { goal: MarketingGoals.homeLearnMore, title: 'Клик «Узнать больше»', surface: 'главная, блок «Как работает наша платформа»' },
  { goal: MarketingGoals.calendarFromEvents, title: 'Клик «Календарь» из блока событий', surface: 'главная, блок ближайших событий' },
  { goal: MarketingGoals.calendarNav, title: 'Клик «Календарь» на сайте', surface: 'шапка, меню, подвал' },
  { goal: MarketingGoals.practicesNav, title: 'Клик «Практики»', surface: 'шапка, меню, подвал' },
  { goal: MarketingGoals.mastersNav, title: 'Клик «Мастера»', surface: 'шапка, меню, подвал' },
  { goal: MarketingGoals.registrationStartPath, title: 'Регистрация через «Начать путь»', surface: 'главная, блок Пути света' },
  { goal: MarketingGoals.registrationLiveSession, title: 'Регистрация через «+ Live-сессия»', surface: 'шапка' },
];

/**
 * Насколько долго цель считается уже отправленной.
 *
 * `device` — состояние, которое случается один раз в жизни устройства
 * (регистрация, активация). Повторная отправка означала бы, что кабинет
 * оптимизируется на людей, которые «регистрируются» каждую неделю.
 *
 * `session` — повторяемое действие. Дедуплицируется в пределах аналитической
 * сессии и по естественному ключу (какая именно практика), чтобы двойной
 * рендер или возврат на экран не удваивали конверсию, но вторая практика за
 * вечер осталась вторым событием.
 */
export type GoalScope = 'device' | 'session';

interface GoalRule {
  events: readonly string[];
  goal: MarketingGoal;
  scope: GoalScope;
  /** Естественный ключ повторяемого действия внутри сессии. */
  keyOf?: (properties: Record<string, unknown> | undefined) => string | undefined;
  /**
   * Дополнительное условие поверх имени события (CORE-1277).
   *
   * Восемь целей Adventum намеренно не завели восемь новых внутренних событий:
   * «Узнать больше» и «Календарь из блока событий» — это один и тот же
   * `home_cta_clicked` с разным `cta`, три пункта навигации — один
   * `header_nav_clicked` с разным `destination`. Различать их признаком внутри
   * события, а не новым именем, — это и есть «переиспользовать существующий
   * сбор», которого требует задача.
   */
  when?: (properties: Record<string, unknown> | undefined) => boolean;
  /**
   * Чем размечается цель в кабинете.
   *
   * Ключ — `goal_source`, и это не описка: `source` уже занят источником
   * кампании из атрибуции CORE-1137, и запись места клика поверх него стёрла бы
   * разрез «откуда пришёл человек» ровно в тех событиях, ради которых кампанию
   * и покупают.
   */
  goalSource?: (properties: Record<string, unknown> | undefined) => string | undefined;
}

/** Имя параметра, которым различаются одноимённые цели. */
export const GOAL_SOURCE_PARAM = 'goal_source';

/** Значение свойства события как строка (свойства приходят нетипизированными). */
const prop = (
  properties: Record<string, unknown> | undefined,
  key: string,
): string | undefined => {
  const value = properties?.[key];
  return typeof value === 'string' && value ? value : undefined;
};

/**
 * Правила отображения внутренних событий во внешние цели.
 *
 * Список умышленно короткий. Внешние кабинеты — не копия продуктовой аналитики:
 * туда идёт то, на чём кампания может обучаться, и каждое лишнее имя здесь —
 * это ещё одна цель, которую кто-то будет сверять с внутренними числами и
 * находить расхождение.
 */
/**
 * Устойчивые идентификаторы действий, на которых стоят цели (CORE-1277).
 *
 * Задача прямо запрещает привязывать цель к видимой надписи или к CSS-классу —
 * и не из вкусовых соображений: надписи главной приходят с бэка (их правит
 * редактор в админке), а «Календарь» на сайте называется так в двух разных
 * местах. Экспортированы отсюда, чтобы место отправки и правило смотрели на
 * ОДНУ строку: разъехавшийся литерал не сломает сборку, он просто тихо
 * перестанет питать цель.
 */
export const MarketingActions = {
  /** CTA «Узнать больше» в блоке «Как работает наша платформа». */
  homeLearnMore: 'how_it_works_about_platform',
  /** Кнопка блока ближайших событий, ведущая в календарь. */
  homeEventsCalendar: 'upcoming_events_calendar',
} as const;

/**
 * Откуда сделан глобальный переход. Метка места, а не отдельная цель: «Клик
 * "Календарь" на сайте» в инструкции один, где бы человек ни нажал.
 */
export const NavSurfaces = {
  header: 'header',
  menu: 'mobile_menu',
  footer: 'footer',
} as const;

export type NavSurface = (typeof NavSurfaces)[keyof typeof NavSurfaces];

const NAV_SURFACES: ReadonlySet<string> = new Set(Object.values(NavSurfaces));

/**
 * Место клика для навигационных целей — или ничего.
 *
 * Здесь стоял `?? NavSurfaces.header`, и это было хуже отсутствия разреза.
 * Клик без `surface` приезжал в кабинет не «пусто», а «из шапки»: неправдой,
 * неотличимой от правды. Пустую ячейку в отчёте видно и о ней спрашивают;
 * подменённое значение не показывает никакой отчёт — оно просто тихо
 * прибавляется не к тому месту. Ровно так дефект CORE-1277 и прожил до QA:
 * мобильное меню теряло `surface`, и весь его трафик учитывался как шапка.
 *
 * Проверяем по списку, а не на непустоту: `surface` приходит нетипизированным
 * из свойств события, и опечатка в будущем отправителе иначе завела бы в
 * кабинете новую «поверхность» и размазала отчёт по целям.
 *
 * Отсутствие параметра цель не отменяет: {@link goalsForEvent} кладёт `params`
 * только когда значение есть, то есть конверсия засчитывается по-прежнему —
 * теряется лишь разрез, и ровно тогда, когда он неизвестен.
 */
const navGoalSource = (properties: Record<string, unknown> | undefined): string | undefined => {
  const surface = prop(properties, 'surface');
  return surface && NAV_SURFACES.has(surface) ? surface : undefined;
};

/**
 * Маршруты, переход на которые считается целью. Имена — из навигации.
 *
 * У календаря их два, и это не дубль: в шапке и подвале «Календарь» ведёт на
 * общее расписание (`Lives`, адрес `/schedule`), а в бургер-меню — в личный
 * календарь (`MyCalendar`). Надпись, намерение и цель у них одни; разными их
 * делает только то, куда приложение решает вести с узкого экрана.
 */
const NAV_DESTINATIONS = {
  calendar: ['Lives', 'MyCalendar'] as readonly string[],
  practices: 'Practices',
  masters: 'Masters',
} as const;

/**
 * Источник регистрационной воронки: какой CTA привёл человека к форме.
 *
 * Живёт здесь, а не рядом с формой, потому что это единственное, чем цели 7 и 8
 * отличаются друг от друга: событие регистрации одно.
 */
export const SIGNUP_SOURCE_PROP = 'signup_source';
export const SignupSources = {
  startPath: 'start_path',
  liveSession: 'live_session',
} as const;

export type SignupSource = (typeof SignupSources)[keyof typeof SignupSources];

export const GOAL_RULES: readonly GoalRule[] = [
  {
    // Именно отправка формы, а не её показ: показ посадочной как «начало
    // регистрации» превращает цель в счётчик трафика, на котором кабинету
    // нечему учиться.
    events: ['signup_submitted'],
    goal: MarketingGoals.registrationStart,
    scope: 'session',
  },
  {
    // Главная рекламная конверсия первого этапа (протокол 11.08, п. 6).
    events: ['signup_success'],
    goal: MarketingGoals.registrationSuccess,
    scope: 'device',
  },
  {
    // «Дошёл до сути продукта»: включил практику или вошёл в Live-сессию.
    // Тот же смысл, что у осмысленного действия в когортах CORE-1138 и у
    // серверного факта в воронках CORE-1140 — три разных определения
    // активации сделали бы сверку невозможной.
    events: ['content_play_started', 'live_room_joined'],
    goal: MarketingGoals.activation,
    scope: 'device',
  },
  {
    events: ['content_play_completed'],
    goal: MarketingGoals.practiceCompleted,
    scope: 'session',
    keyOf: (props) => {
      const id = props?.content_id ?? props?.contentId;
      return id == null ? undefined : String(id);
    },
  },

  // ── Восемь целей Adventum (CORE-1277) ─────────────────────────────────────
  //
  // Все семь правил ниже сидят на уже существующих событиях: `home_cta_clicked`
  // из CORE-595 и `header_nav_clicked` из шапки. Восьмая цель — «посетил сайт» —
  // событием не питается вовсе, её отдаёт `siteVisitGoal()`: внутреннего
  // события «человек открыл страницу» нет и заводить его незачем, факт визита
  // уже известен слою по тому, что он загрузился.
  //
  // Область видимости у кликов — сессия. Второй клик по «Практикам» за тот же
  // визит не новая конверсия, а тот же интерес; критерий «ровно одна цель на
  // действие» про то, что действие не рождает двух целей и не молчит, а не про
  // отмену дедупликации, которую задача велит сохранить.
  {
    events: ['home_cta_clicked'],
    goal: MarketingGoals.homeLearnMore,
    scope: 'session',
    when: (props) => prop(props, 'cta') === MarketingActions.homeLearnMore,
  },
  {
    events: ['home_cta_clicked'],
    goal: MarketingGoals.calendarFromEvents,
    scope: 'session',
    when: (props) => prop(props, 'cta') === MarketingActions.homeEventsCalendar,
    goalSource: () => 'home_events',
  },
  {
    events: ['header_nav_clicked'],
    goal: MarketingGoals.calendarNav,
    scope: 'session',
    when: (props) => {
      const destination = prop(props, 'destination');
      return !!destination && NAV_DESTINATIONS.calendar.includes(destination);
    },
    goalSource: navGoalSource,
  },
  {
    events: ['header_nav_clicked'],
    goal: MarketingGoals.practicesNav,
    scope: 'session',
    when: (props) => prop(props, 'destination') === NAV_DESTINATIONS.practices,
    goalSource: navGoalSource,
  },
  {
    events: ['header_nav_clicked'],
    goal: MarketingGoals.mastersNav,
    scope: 'session',
    when: (props) => prop(props, 'destination') === NAV_DESTINATIONS.masters,
    goalSource: navGoalSource,
  },

  // Обе воронки заканчиваются ПОДТВЕРЖДЁННЫМ `signup_success` — тем же
  // событием, что и общая цель `registration_success` выше. Заполненная форма,
  // закрытое окно и ошибка регистрации до него не доходят, поэтому отдельного
  // «не считать незавершённое» правила не требуется: его обеспечивает выбор
  // события, а не проверка внутри.
  {
    events: ['signup_success'],
    goal: MarketingGoals.registrationStartPath,
    scope: 'device',
    when: (props) => prop(props, SIGNUP_SOURCE_PROP) === SignupSources.startPath,
    goalSource: () => SignupSources.startPath,
  },
  {
    events: ['signup_success'],
    goal: MarketingGoals.registrationLiveSession,
    scope: 'device',
    when: (props) => prop(props, SIGNUP_SOURCE_PROP) === SignupSources.liveSession,
    goalSource: () => SignupSources.liveSession,
  },
];

export interface GoalHit {
  goal: MarketingGoal;
  scope: GoalScope;
  /** Ключ дедупликации в пределах области видимости. */
  dedupKey: string;
  /** Разметка цели сверх контекста кампании. */
  params?: Record<string, string>;
}

/**
 * Во что превращается внутреннее событие. Пустой список — ни во что.
 *
 * Список, а не одна цель, с CORE-1277: успешная регистрация из CTA «Начать
 * путь» — это И общая конверсия `registration_success`, на которой кампания
 * училась до сих пор, И отдельная цель воронки, которую просит Adventum.
 * Оставить одну означало бы либо потерять существующую цель в кабинете, либо
 * не сделать новую.
 */
export function goalsForEvent(
  eventName: string,
  properties?: Record<string, unknown>,
): GoalHit[] {
  const hits: GoalHit[] = [];
  for (const rule of GOAL_RULES) {
    if (!rule.events.includes(eventName)) continue;
    if (rule.when && !rule.when(properties)) continue;
    const natural = rule.keyOf?.(properties);
    const source = rule.goalSource?.(properties);
    hits.push({
      goal: rule.goal,
      scope: rule.scope,
      dedupKey: natural ? `${rule.goal}:${natural}` : rule.goal,
      ...(source ? { params: { [GOAL_SOURCE_PARAM]: source } } : {}),
    });
  }
  return hits;
}

/**
 * Цель «посетил сайт» (CORE-1277).
 *
 * Единственная из восьми, у которой нет действия человека: её событие — сам
 * факт, что страница прода открылась и слой включился. Раз в сессию, потому что
 * визит — это визит, а не количество внутренних переходов в SPA.
 */
export function siteVisitGoal(): GoalHit {
  return {
    goal: MarketingGoals.siteVisit,
    scope: 'session',
    dedupKey: MarketingGoals.siteVisit,
  };
}

// ── Первый просмотр Метрики ─────────────────────────────────────────────────

/** Что из query посадочной доезжает до Метрики: метки кампании и click-id Директа. */
const LANDING_QUERY_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'yclid'];

/**
 * Адрес и реферер первого просмотра, который уходит в Метрику.
 *
 * Счётчик грузится с `defer: true`, и просмотры шлёт сам рантайм — раньше
 * адресом `origin + pathname`, без query. Источник визита Метрика берёт из
 * первого просмотра, поэтому метки кампании в её отчёт не попадали вовсе:
 * QA 29.09 (CORE-1278) — фильтр «UTM Campaign» пуст, хотя цели с параметром
 * campaign дошли. К моменту согласия React Navigation уже переписал адрес,
 * так что источник — снимок входа из `acquisition.ts`, а не текущий location.
 *
 * Query переносится по белому списку, а не целиком: в нём бывают токены
 * сброса пароля и приглашений. У реферера отрезаются query и фрагмент.
 */
export function metrikaLandingHit(
  origin: string,
  entry: { pathname: string; search: string; referrer: string } | null,
): { path: string; url: string; referer: string } {
  const path = entry?.pathname || '/';
  const params = new URLSearchParams(entry?.search || '');
  const kept = new URLSearchParams();
  for (const key of LANDING_QUERY_KEYS) {
    const value = params.get(key);
    if (value) kept.set(key, value);
  }
  const query = kept.toString();

  let referer = '';
  if (entry?.referrer) {
    try {
      const ref = new URL(entry.referrer);
      referer = ref.origin + ref.pathname;
    } catch {
      referer = '';
    }
  }

  return { path, url: origin + path + (query ? `?${query}` : ''), referer };
}

// ── Параметры цели ──────────────────────────────────────────────────────────

/**
 * Что уезжает вместе с целью.
 *
 * Белый список из пяти полей, а не «весь контекст привлечения». Из атрибуции
 * CORE-1137 сюда осознанно НЕ попадают:
 *
 *   • `anonId` — псевдоним устройства. Кабинету он не нужен: сводка строится
 *     по паре «дата × кампания», а не по людям, и именно так внутренние
 *     агрегаты стыкуются с расходом (см. серверную выгрузку). Передавать
 *     идентификатор устройства ради удобства джойна — это отдать наружу
 *     сквозной ключ, которого там быть не обязано.
 *
 *   • сам `clickId` (yclid/fbclid/…). Вид площадки полезен для разреза, а
 *     значение нужно только для загрузки офлайн-конверсий — а это отдельная
 *     история с отдельным согласованием. Значение при этом никуда не пропало:
 *     оно лежит на сервере в `AcquisitionAttributions`.
 *
 *   • `landingPath` и `referrerHost` — адреса, которые внутри продукта
 *     регулярно содержат идентификаторы (профиль, комната, пост).
 *
 * Значения уже просеяны `scrubValue` на входе (CORE-1137): всё, похожее на
 * email или телефон, отброшено ЦЕЛИКОМ ещё до попадания в состояние.
 */
export function goalParams(state: AcquisitionState | null): Record<string, string> {
  const params: Record<string, string> = {};
  if (!state) return params;
  const put = (key: string, value: string | undefined) => {
    if (value) params[key] = value;
  };
  put('source', state.last.source);
  put('medium', state.last.medium);
  put('campaign', state.last.campaign);
  put('content', state.last.content);
  put('click_id_kind', state.last.clickIdKind);
  return params;
}

/** Поля, которые не должны появиться в маркетинговом событии ни при каких условиях. */
export const FORBIDDEN_PARAM_KEYS: readonly string[] = [
  'email', 'e_mail', 'phone', 'tel', 'name', 'firstName', 'lastName',
  'userId', 'user_id', 'anonId', 'anon_id', 'clickId', 'click_id',
  'token', 'message', 'text', 'query',
];

/**
 * Последний рубеж перед отправкой: выкинуть запрещённые ключи.
 *
 * До CORE-1277 список выше был декларацией — его проверял тест, но в путь
 * отправки он не входил, потому что параметры собирала одна функция с белым
 * списком из пяти полей. Теперь параметры добавляет ещё и правило цели, и
 * между «белый список» и «что реально уехало» появился зазор. Сверка идёт по
 * нормализованному ключу (`user_id`, `userId`, `USER-ID` — одно и то же),
 * иначе запрет обходится сменой регистра.
 */
export function sanitizeGoalParams(params: Record<string, string>): Record<string, string> {
  const normalize = (key: string) => key.toLowerCase().replace(/[^a-z0-9]/g, '');
  const forbidden = new Set(FORBIDDEN_PARAM_KEYS.map(normalize));
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (forbidden.has(normalize(key))) continue;
    out[key] = value;
  }
  return out;
}

// ── Дедупликация ────────────────────────────────────────────────────────────

export const MARKETING_DEDUP_KEY = 'agape_marketing_sent_v1';

export interface DedupLedger {
  /** Цели, отправленные один раз за жизнь устройства. */
  device: string[];
  /** Аналитическая сессия, к которой относится `sessionKeys`. */
  sessionId: string;
  sessionKeys: string[];
}

/** Потолок на случай, если сессия окажется долгой, а действий много. */
const MAX_SESSION_KEYS = 100;

export function emptyLedger(sessionId: string): DedupLedger {
  return { device: [], sessionId, sessionKeys: [] };
}

/**
 * Свести журнал отправок с новой целью.
 *
 * Возвращает `send: false`, если цель в своей области видимости уже уходила.
 * Смена сессии обнуляет только сессионную половину — устройственная переживает
 * и смену сессии, и перезагрузку.
 */
export function applyGoal(
  ledger: DedupLedger | null,
  sessionId: string,
  hit: GoalHit,
): { ledger: DedupLedger; send: boolean } {
  const base: DedupLedger = ledger && Array.isArray(ledger.device)
    ? {
      device: ledger.device,
      sessionId: ledger.sessionId === sessionId ? sessionId : sessionId,
      sessionKeys: ledger.sessionId === sessionId && Array.isArray(ledger.sessionKeys)
        ? ledger.sessionKeys
        : [],
    }
    : emptyLedger(sessionId);

  if (hit.scope === 'device') {
    if (base.device.includes(hit.dedupKey)) return { ledger: base, send: false };
    return { ledger: { ...base, device: [...base.device, hit.dedupKey] }, send: true };
  }

  if (base.sessionKeys.includes(hit.dedupKey)) return { ledger: base, send: false };
  const keys = [...base.sessionKeys, hit.dedupKey];
  return {
    ledger: { ...base, sessionKeys: keys.slice(-MAX_SESSION_KEYS) },
    send: true,
  };
}

// ── Статус загрузки Метрики ─────────────────────────────────────────────────

/**
 * CORE-1853 — загрузилась ли у человека Метрика, а если нет — почему.
 *
 * Наша аналитика видела переход по метке, а Метрика — нет, и отличить «сеть
 * или блокировщик режут mc.yandex.ru» от «счётчик не грузился по нашим
 * правилам» было нечем. Событие идёт в нашу же аналитику с тем же `sessionId`,
 * что и `acquisition_touch`, и админка показывает его у визита.
 */
export const METRIKA_STATUS_EVENT = 'marketing_tags_status';

/** Сколько ждать решения о загрузке, прежде чем сказать «не загрузилась». */
export const METRIKA_STATUS_SETTLE_MS = 10_000;

/** Сколько ждать `tag.js`, прежде чем считать загрузку зависшей. */
export const METRIKA_SCRIPT_TIMEOUT_MS = 15_000;

export type MetrikaOutcome = 'loaded' | 'script_error' | 'script_timeout' | 'not_loaded';

export interface MetrikaStatusProps {
  metrika: MetrikaOutcome;
  /** Причина из `shouldLoadTags`; у начатой загрузки — `ok`. */
  reason: string;
  consent: 'unknown' | 'blocked' | 'choice';
  region: TagRegion;
  /** Сколько грузился `tag.js`, мс. Только у `loaded` и `script_error`. */
  scriptMs?: number;
}

/**
 * Не больше двух событий на загрузку страницы: «не загрузилась» после паузы,
 * если загрузка так и не началась, и итог `tag.js`, если она началась —
 * в том числе позже (регион пришёл после паузы). Админка берёт последнее.
 */
export class MetrikaStatusTracker {
  private reported: 'none' | 'not_loaded' | 'final' = 'none';
  private startedAt: number | null = null;
  private reason = 'pending';
  private consent: MarketingConsent = 'unknown';
  private region: TagRegion = 'unknown';

  /** Запомнить последнее решение гейта. */
  note(reason: string, consent: MarketingConsent, region: TagRegion): void {
    this.reason = reason;
    this.consent = consent;
    this.region = region;
  }

  /** Загрузка `tag.js` началась. Повторный вызов ничего не меняет. */
  scriptStarted(now: number): void {
    if (this.startedAt === null) this.startedAt = now;
  }

  /** Пауза истекла: если загрузка не началась — это и есть ответ. */
  settle(): MetrikaStatusProps | null {
    if (this.reported !== 'none' || this.startedAt !== null) return null;
    this.reported = 'not_loaded';
    return this.props('not_loaded', this.reason);
  }

  /** Чем кончилась загрузка `tag.js`. Учитывается только первый итог. */
  scriptFinished(outcome: Exclude<MetrikaOutcome, 'not_loaded'>, now: number): MetrikaStatusProps | null {
    if (this.reported === 'final' || this.startedAt === null) return null;
    this.reported = 'final';
    const props = this.props(outcome, 'ok');
    if (outcome !== 'script_timeout') props.scriptMs = Math.max(0, Math.round(now - this.startedAt));
    return props;
  }

  private props(metrika: MetrikaOutcome, reason: string): MetrikaStatusProps {
    const consent = typeof this.consent === 'object' ? 'choice' : this.consent;
    return { metrika, reason, consent, region: this.region };
  }
}
